import {useEffect, useState, useRef} from 'react';
import {OAUTH_CONFIG, AUTH_SETTINGS, isElectronEnvironment} from '../config/auth';

// Helper functions for secure storage with localStorage fallback
const secureStorage = {
	// OAuth state
	async setOAuthState(state) {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.setOAuthState(state);
		} else {
			localStorage.setItem('oauth_state', state);
			return true;
		}
	},

	async getOAuthState() {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.getOAuthState();
		} else {
			return localStorage.getItem('oauth_state');
		}
	},

	async clearOAuthState() {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.clearOAuthState();
		} else {
			localStorage.removeItem('oauth_state');
			return true;
		}
	},

	// Auth bypass
	async setAuthBypass(enabled) {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.setAuthBypass(enabled);
		} else {
			localStorage.setItem('auth_bypass', enabled.toString());
			return true;
		}
	},

	async getAuthBypass() {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.getAuthBypass();
		} else {
			return localStorage.getItem('auth_bypass') === 'true';
		}
	},

	// Auth data
	async setAuth(authData) {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.setAuth(authData);
		} else {
			localStorage.setItem('auth_token', authData.accessToken);
			localStorage.setItem('token_expiry', authData.tokenExpiry.toString());
			if (authData.refreshToken) {
				localStorage.setItem('refresh_token', authData.refreshToken);
			}
			return true;
		}
	},

	async getAuth() {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.getAuth();
		} else {
			const token = localStorage.getItem('auth_token');
			const expiry = localStorage.getItem('token_expiry');
			const refreshToken = localStorage.getItem('refresh_token');
			
			if (token && expiry) {
				return {
					accessToken: token,
					refreshToken: refreshToken,
					tokenExpiry: parseInt(expiry)
				};
			}
			return null;
		}
	},

	async clearAuth() {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.clearAuth();
		} else {
			localStorage.removeItem('auth_token');
			localStorage.removeItem('token_expiry');
			localStorage.removeItem('refresh_token');
			return true;
		}
	},

	// User profile
	async setUserProfile(profile) {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.setUserProfile(profile);
		} else {
			localStorage.setItem('user_data', JSON.stringify(profile));
			return true;
		}
	},

	async getUserProfile() {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.getUserProfile();
		} else {
			const userData = localStorage.getItem('user_data');
			return userData ? JSON.parse(userData) : null;
		}
	},

	async getCompany() {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.getCompany();
		} else {
			const companyData = localStorage.getItem('company_data');
			return companyData ? JSON.parse(companyData) : null;
		}
	},

	async setCompany(company) {

		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.setCompany(company);
		} else {
			localStorage.setItem('company_data', JSON.stringify(company));
			return true;
		}
	},

	// Utility
	async isAuthenticated() {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.isAuthenticated();
		} else {
			const token = localStorage.getItem('auth_token');
			const expiry = localStorage.getItem('token_expiry');
			return !!(token && expiry && new Date().getTime() < parseInt(expiry));
		}
	},

	async getAccessToken() {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.getAccessToken();
		} else {
			return localStorage.getItem('auth_token');
		}
	},

	async getRefreshToken() {
		if (isElectronEnvironment() && window.electronAPI?.secureStorage) {
			return await window.electronAPI.secureStorage.getRefreshToken();
		} else {
			return localStorage.getItem('refresh_token');
		}
	}
};

export const useAuth = () => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [user, setUser] = useState(null);
	const [company, setCompany] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	
	// Use ref to track OAuth processing state across re-renders
	const isProcessingOAuthRef = useRef(false);

	// Check for existing authentication on mount
	useEffect(() => {
		const checkAuth = async () => {
			try {
				// DEVELOPMENT BYPASS: Check for bypass flag
				const bypassAuth = await secureStorage.getAuthBypass();
				if (bypassAuth) {
					// console.log('🔓 AUTH BYPASS ENABLED - Using test user');
					setIsAuthenticated(true);
					setUser({
						id: 'test-user-123',
						name: 'Test User',
						email: 'test@example.com',
						role: 'admin'
					});
					return;
				}

				// Check for existing authentication
				const isAuth = await secureStorage.isAuthenticated();
				if (isAuth) {
					const authData = await secureStorage.getAuth();
					const userProfile = await secureStorage.getUserProfile();
					const companyData = await secureStorage.getCompany();
					
					if (authData && userProfile) {
						setIsAuthenticated(true);
						setUser(userProfile);

						if(companyData) {
							setCompany(companyData);
						}

						// console.log('✅ Authentication restored from secure storage');
					}
				}
		} catch (error) {
			console.error('❌ Failed to check authentication:', error);
		}
		};

		checkAuth();

		// Listen for OAuth success events from main process
		if (isElectronEnvironment() && window.electronAPI && window.electronAPI.onMessage) {
			// Listen for main process logs
			window.electronAPI.onMessage('main-process-log', (event, data) => {
				// console.log('🔧 Main Process Log:', data);
			});
			
			const handleOAuthSuccess = async (event, data) => {
				// Prevent duplicate processing using ref
				if (isProcessingOAuthRef.current) {
					// console.log('⚠️ OAuth success already being processed, ignoring duplicate event');
					return;
				}
				
				isProcessingOAuthRef.current = true;
				
				let state = null;
				let storedState = null;
				
				try {
					// console.log('🔐 OAuth success event received:', data);
					const { tokenData, state: oauthState } = data;
					state = oauthState;
					storedState = await secureStorage.getOAuthState();
					
					// console.log('State validation:', { received: state, stored: storedState, match: state === storedState });

					// console.log('state', state);
					// console.log('storedState', storedState);

					// console.log('tokenData', tokenData);
					
					// Validate state parameter
					if (state === storedState) {
						await secureStorage.clearOAuthState();
						
						// Store tokens securely
						const expiryTime = new Date().getTime() + (tokenData.expires_in * 1000);
						const authData = {
							accessToken: tokenData.access_token,
							refreshToken: tokenData.refresh_token,
							tokenExpiry: expiryTime,
							tokenType: tokenData.token_type
						};

						await secureStorage.setAuth(authData);
						// console.log('✅ Tokens stored securely');

						// Fetch user profile
						await fetchUserProfile(tokenData.access_token);

						// console.log('✅ User profile fetched successfully');

						// Fetch company
						// Set in settings now
						// await fetchCompany(tokenData.access_token);

						// Don't navigate - let React's state management handle the screen change
						// The isAuthenticated state change will trigger the useEffect in App.js
						// which will automatically show the home screen
						
					} else {
						console.error('❌ OAuth state validation failed');
						setError('Authentication failed: Invalid state parameter');
					}
				} catch (error) {
					console.error('❌ OAuth success handling failed:', error);
					setError('Authentication failed. Please try again.');
				} finally {
					isProcessingOAuthRef.current = false;
				}
			};

			const handleAuthTokenRefreshFailed = async () => {
				// console.log('🔄 Token refresh failed, logging out user');
				await clearAuthData();
				setError('Your session has expired. Please log in again.');
			};
			
			window.electronAPI.onMessage('oauth-success', handleOAuthSuccess);
			window.electronAPI.onMessage('auth-token-refresh-failed', handleAuthTokenRefreshFailed);
			
			// Cleanup function to remove event listeners
			return () => {
				if (window.electronAPI && window.electronAPI.removeAllListeners) {
					window.electronAPI.removeAllListeners('oauth-success');
					window.electronAPI.removeAllListeners('auth-token-refresh-failed');
					window.electronAPI.removeAllListeners('main-process-log');
				}
			};
		}
	}, []);

	// Note: OAuth callback is now handled through main process events
	// No need for URL-based callback handling in Electron

	const clearAuthData = async () => {
		await secureStorage.clearAuth();
		// console.log('✅ Auth data cleared securely');
		
		setIsAuthenticated(false);
		setUser(null);
	};

	// DEVELOPMENT BYPASS: Enable authentication bypass
	const enableAuthBypass = async () => {
		await secureStorage.setAuthBypass(true);
		setIsAuthenticated(true);
		setUser({
			id: 'test-user-123',
			name: 'Test User',
			email: 'test@example.com',
			role: 'admin'
		});
		// console.log('🔓 AUTH BYPASS ENABLED');
	};

	// DEVELOPMENT BYPASS: Disable authentication bypass
	const disableAuthBypass = async () => {
		await secureStorage.setAuthBypass(false);
		setIsAuthenticated(false);
		setUser(null);
		// console.log('🔒 AUTH BYPASS DISABLED');
	};

	const generateRandomString = (length) => {
		const array = new Uint32Array(length / 2);
		crypto.getRandomValues(array);
		const state = Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
		// console.log('🔐 Generated state:', state);
		return state;
	};

	const handleOAuthLogin = async () => {
		setLoading(true);
		setError(null);

		try {
			// Generate state parameter for CSRF protection
			const state = generateRandomString(16);
			await secureStorage.setOAuthState(state);

			// console.log('🔐 Starting OAuth login with state:', state);

			// Build authorization URL
			const authParams = new URLSearchParams({
				response_type: 'code',
				client_id: OAUTH_CONFIG.clientId,
				redirect_uri: OAUTH_CONFIG.redirectUri,
				scope: OAUTH_CONFIG.scope,
				state: state,
			});

			const authUrl = `${OAUTH_CONFIG.authorizationUrl}?${authParams.toString()}`;

			// Check if we're in Electron
			if (isElectronEnvironment()) {
				// Use Electron's OAuth flow with new OAuth manager
				try {
					// console.log('🚀 Initiating Electron OAuth flow...');
					const result = await window.electronAPI.openOAuth(authUrl, OAUTH_CONFIG.redirectUri, state);
					// console.log('✅ OAuth flow initiated successfully');
					// The oauth-success event will handle the rest of the flow
				} catch (electronError) {
					console.error('❌ Electron OAuth failed:', electronError);
					setError('Authentication failed. Please try again.');
				}
			} else {
				// Web version requires backend proxy for security
				setError('Authentication requires Electron app or backend proxy for security. Please use the Electron application.');
			}

		} catch (error) {
			console.error('❌ OAuth login failed:', error);
			setError('Failed to initiate OAuth login');
		} finally {
			setLoading(false);
		}
	};

	const exchangeCodeForToken = async (code) => {
		setLoading(true);
		setError(null);

		// console.log('Exchanging code for token');

		try {
			let tokenData;

			// Use secure Electron API if available
			if (isElectronEnvironment() && window.electronAPI.exchangeCodeForToken) {
				// Secure token exchange through Electron main process
				tokenData = await window.electronAPI.exchangeCodeForToken(code);
			} else {
				// Web version requires a backend proxy for security
				throw new Error('Authentication requires Electron app or backend proxy for security. Client secret cannot be exposed in browser.');
			}

			// Store tokens
			const expiryTime = new Date().getTime() + (tokenData.expires_in * 1000);
			localStorage.setItem('auth_token', tokenData.access_token);
			localStorage.setItem('token_expiry', expiryTime.toString());

			if (tokenData.refresh_token) {
				localStorage.setItem('refresh_token', tokenData.refresh_token);
			}
			// console.log('Logged In');

			// Fetch user profile
			//await fetchUserProfile(tokenData.access_token);
			

			// Navigate back to main app after successful authentication
			if (isElectronEnvironment()) {
				// In Electron, use the navigation API if available
				if (window.electronAPI && window.electronAPI.navigateTo) {
					await window.electronAPI.navigateTo('/');
				} else {
					// Fallback to window.location
					window.location.href = '/';
				}
			} else {
				// In web, clean up URL
				window.history.replaceState({}, document.title, '/');
			}

		} catch (error) {
			console.error('Token exchange failed:', error);
			setError('Authentication failed. Please try again.');
			clearAuthData();
		} finally {
			setLoading(false);
		}
	};

	// const fetchCompany = async (token) => {
	// 	// console.log('🔍 Fetching company...');
	// 	try {
	// 		const companyResponse = await window.electronAPI.request('/company', 'GET', null, 
	// 			{
	// 				'Accept': 'application/json',
	// 			});

	// 			if(companyResponse.ok) {
	// 				const companyData = await companyResponse.body;

	// 				// console.log('✅ Company fetched:', companyData);

	// 				// Store company data securely
	// 				await secureStorage.setCompany(companyData);
	// 			} else {
	// 				console.warn('⚠️ Company endpoint not available');
	// 				throw new Error(`Company fetch failed: ${companyResponse.statusText}`);
	// 			}
	// 	} catch (error) {
	// 		console.error('Failed to fetch company:', error);
	// 	}
	// }

	const fetchUserProfile = async (token) => {
		try {
			// console.log('🔍 Fetching user profile...');
			
			// Try to fetch user profile from AggMaps API
			const profileResponse = await window.electronAPI.request('/me', 'GET', null, 
				{
					'Accept': 'application/json',
				});

			// console.log('profileResponse', profileResponse);

			if (profileResponse.ok) {
				const userData = profileResponse.body;
				// console.log('✅ User profile fetched:', userData);

				// Store user data securely
				await secureStorage.setUserProfile(userData);
				// console.log('✅ User profile stored securely');
				
				setUser(userData);
				setIsAuthenticated(true);
			} else {
				console.warn('⚠️ Profile endpoint not available, using minimal user data');
				throw new Error(`Profile fetch failed: ${profileResponse.statusText}`);
			}

		} catch (error) {
			console.warn('⚠️ Failed to fetch user profile, using minimal user data:', error);
			// If profile fetch fails, we still have a valid token
			// Create minimal user object
			const minimalUser = {
				id: 'user-' + Date.now(),
				name: 'Authenticated User',
				email: 'user@example.com',
				role: 'user'
			};
			
			// Store minimal user data securely
			await secureStorage.setUserProfile(minimalUser);
			// console.log('✅ Minimal user data stored securely');
			
			setUser(minimalUser);
			setIsAuthenticated(true);
			// console.log('✅ Using minimal user data:', minimalUser);
		}
	};

	const refreshAccessToken = async () => {
		const refreshToken = await secureStorage.getRefreshToken();

		if (!refreshToken) {
			await logout();
			return false;
		}

		try {
			let tokenData;

			// Use secure Electron API if available
			if (isElectronEnvironment() && window.electronAPI.refreshAccessToken) {
				// Secure token refresh through Electron main process
				tokenData = await window.electronAPI.refreshAccessToken(refreshToken);
			} else {
				// Web version requires a backend proxy for security
				throw new Error('Token refresh requires Electron app or backend proxy for security. Client secret cannot be exposed in browser.');
			}

			// Update stored tokens securely
			const expiryTime = new Date().getTime() + (tokenData.expires_in * 1000);
			const authData = {
				accessToken: tokenData.access_token,
				refreshToken: tokenData.refresh_token,
				tokenExpiry: expiryTime,
				tokenType: tokenData.token_type
			};

			await secureStorage.setAuth(authData);
			return true;

		} catch (error) {
			console.error('Token refresh failed:', error);
			await logout();
			return false;
		}
	};

	const logout = async () => {
		setLoading(true);

		try {
			const token = await secureStorage.getAccessToken();

			// Optionally revoke token on server
			if (token) {
				try {
					await window.electronAPI.request('https://aggmaps.com/oauth/v2/revoke', 'POST', 
						new URLSearchParams({ token: token }).toString(),
						{
							'Content-Type': 'application/x-www-form-urlencoded',
						}
					);
				} catch (error) {
					console.warn('Token revocation failed:', error);
					// Continue with logout even if revocation fails
				}
			}

			// Use the main process logout handler which stops the token refresh timer
			if (isElectronEnvironment() && window.electronAPI.logout) {
				await window.electronAPI.logout();
			}

			await clearAuthData();

		} catch (error) {
			console.error('Logout error:', error);
			// Clear local data even if server logout fails
			await clearAuthData();
		} finally {
			setLoading(false);
		}
	};

	const getAuthHeaders = async () => {
		const token = await secureStorage.getAccessToken();
		return token ? { Authorization: `Bearer ${token}` } : {};
	};

	const makeAuthenticatedRequest = async (url, options = {}) => {
		// Use the Electron API request which automatically handles authentication
		const method = options.method || 'GET';
		const body = options.body || null;
		const headers = options.headers || {};
		
		// Remove Authorization header since it's handled automatically
		delete headers.Authorization;
		
		return window.electronAPI.request(url, method, body, headers);
	};

	return {
		isAuthenticated,
		user,
		company,
		setCompany,
		loading,
		error,
		handleOAuthLogin,
		logout,
		getAuthHeaders,
		makeAuthenticatedRequest,
		refreshAccessToken,
		// DEVELOPMENT BYPASS: Functions for testing
		enableAuthBypass,
		disableAuthBypass,
	};
};